import React, { useState } from "react";
import { DownloadIcon, HeartIcon, Share2Icon, Trash2Icon } from "lucide-react";
import { HeartFilled } from "./icons";

export default function ImageGallery({ setImage, setDescription }) {
  const initialImages = [
    {
      url: "https://images.unsplash.com/photo-1556905055-8f358a7a47b2",
      text: "Casual summer fashion - woman",
    },
    {
      url: "https://images.unsplash.com/photo-1541099649105-f69ad21f3246",
      text: "Winter jacket - man",
    },
    {
      url: "https://images.unsplash.com/photo-1503342217505-b0a15ec3261c",
      text: "Street style - man",
    },
    {
      url: "https://images.unsplash.com/photo-1520975916090-3105956dac38",
      text: "Traditional outfit - woman",
    },
    {
      url: "https://images.unsplash.com/photo-1520975916090-3105956dac38",
      text: "Traditional outfit - woman",
    },
  ];

  const [images, setImages] = useState(initialImages);
  const [liked, setLiked] = useState(Array(initialImages.length).fill(false));
  const [selectedIndex, setSelectedIndex] = useState(null);

  const toggleLike = (index) => {
    const newLiked = [...liked];
    newLiked[index] = !newLiked[index];
    setLiked(newLiked);
  };

  const downloadImage = async (url, filename) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || "image.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const shareImage = async (url, text) => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "Check this out!", text, url });
      } else {
        navigator.clipboard.writeText(url);
      }
    } catch (error) {
      console.error("Sharing failed:", error);
    }
  };

  const deleteImage = (index) => {
    const updatedImages = [...images];
    updatedImages.splice(index, 1);
    setImages(updatedImages);

    const updatedLikes = [...liked];
    updatedLikes.splice(index, 1);
    setLiked(updatedLikes);

    if (selectedIndex === index) {
      setSelectedIndex(null);
      setImage(null);
    }
  };

  return (
    <div className="space-y-2">
      {images.map((img, idx) => {
        const isSelected = selectedIndex === idx;
        return (
          <div
            key={idx}
            className={`relative group border rounded-xl overflow-hidden shadow-sm ${
              isSelected ? "ring-2 ring-red-200" : ""
            }`}
            onClick={() => {
              setSelectedIndex(idx);
              setImage(img.url);
              setDescription(img.text);
            }}
          >
            <img
              src={img.url}
              alt={img.text}
              className="w-full h-full object-cover cursor-pointer transition-transform duration-300 ease-in-out group-hover:scale-105 group-hover:rotate-2"
            />

            <div
              className={`absolute inset-0 bg-black/50 transition-opacity duration-300 flex flex-col justify-end p-4 ${
                isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
              }`}
            >
              <p className="text-white text-sm mb-2">{img.text}</p>

              {/* Heart*/}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLike(idx);
                }}
                className="absolute top-4 right-2"
              >
                {liked[idx] ? (
                  <HeartFilled size="1em" className="text-red-500 text-lg" />
                ) : (
                  <HeartIcon size="1em" className="text-white text-lg" />
                )}
              </button>

              {/* Download */}
              <button
                className="absolute top-11 right-2 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  downloadImage(img.url, `image-${idx + 1}.jpg`);
                }}
              >
                <DownloadIcon size="1em" className="text-white text-xl" />
              </button>

              {/* Share */}
              <button
                className="absolute top-20 right-2 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  shareImage(img.url, img.text);
                }}
              >
                <Share2Icon size="1em" className="text-white text-xl" />
              </button>

              {/* Delete */}
              <button
                className="absolute top-32 right-2 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteImage(idx);
                }}
              >
                <Trash2Icon size="1em" className="text-white text-xl" />
              </button>

              {/* Action Buttons */}
              <div className="flex gap-2 mt-2 flex-wrap">
                {["Try On", "Generate", "Reimagine", "Studio"].map((label) => (
                  <button
                    key={label}
                    className="bg-pink-600 text-white px-3 py-1 rounded hover:bg-pink-700 active:bg-pink-800"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log(`${label} clicked`);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
